import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BookConsultModal from '../components/BookConsultModal';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar, Headphones, Monitor, Shield, Cloud, Database, Users, CheckCircle } from 'lucide-react';

const ManagedITPage = () => {
  const [showModal, setShowModal] = useState(false);

  const included = [
    { id: 'helpdesk', icon: Headphones, title: 'Helpdesk Support', desc: 'Email and phone support from real people who know your environment' },
    { id: 'remote', icon: Monitor, title: 'Remote Support', desc: 'Quick remote troubleshooting and fixes without waiting for on-site visits' },
    { id: 'monitoring', icon: Monitor, title: 'Device Monitoring', desc: 'Proactive monitoring to catch issues before they become problems' },
    { id: 'patching', icon: Shield, title: 'Patching', desc: 'Regular updates to keep your systems secure and running smoothly' },
    { id: 'o365', icon: Cloud, title: 'Microsoft 365 Management', desc: 'User administration, security settings, and ongoing optimization' },
    { id: 'security', icon: Shield, title: 'Security Baseline', desc: 'MFA enforcement, policies, and security best practices' },
    { id: 'backup', icon: Database, title: 'Backup Oversight', desc: 'Monitoring and verification of your backup systems' },
    { id: 'vendor', icon: Users, title: 'Vendor Coordination', desc: 'We work with your other vendors so you don\'t have to' },
    { id: 'reviews', icon: CheckCircle, title: 'Quarterly Reviews', desc: 'Regular check-ins to review performance and plan ahead' }
  ];

  const responseTimes = [
    { id: 'standard', title: 'Standard', time: 'Within 1 business day', desc: 'For non-urgent requests and questions' },
    { id: 'urgent', title: 'Urgent', time: 'Same-day target', desc: 'For issues affecting productivity' },
    { id: 'emergency', title: 'Emergency', time: '24/7 available', desc: 'For critical outages and security incidents' }
  ];

  const onboardingSteps = [
    { id: 'step-1', number: '1', title: 'Audit + Documentation', desc: 'We document your entire environment—devices, accounts, network, and vendors.' },
    { id: 'step-2', number: '2', title: 'Security Baseline', desc: 'We implement MFA, review permissions, and apply security policies.' },
    { id: 'step-3', number: '3', title: 'Monitoring + Backups', desc: 'We set up monitoring tools and verify your backup systems are working.' },
    { id: 'step-4', number: '4', title: 'Roadmap', desc: 'We create a prioritized plan for improvements and ongoing maintenance.' }
  ];

  const faqs = [
    { id: 'faq-user', q: 'What counts as a user?', a: 'Each staff member who needs access to email, devices, files, and support counts as a user. Shared/front-desk devices can be scoped separately.' },
    { id: 'faq-mac', q: 'Do you support Macs?', a: 'Yes! We support both Windows and Mac environments, as well as mixed environments.' },
    { id: 'faq-current', q: 'Can you work with our current IT?', a: 'Absolutely. We can supplement your existing IT staff or work alongside other vendors. We\'re flexible.' },
    { id: 'faq-hours', q: 'Do you do after-hours support?', a: 'Yes. Emergency support is available 24/7. Standard support is during business hours with same-day response for urgent issues.' },
    { id: 'faq-start', q: 'How fast can we start?', a: 'Most clients are onboarded within 1-2 weeks. We can move faster for urgent situations.' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">Managed IT That Keeps Your Business Running</h1>
          <p className="text-xl text-gray-600 mb-8">
            Support, monitoring, patching, and security for organizations with 1–50 users.
          </p>
          <Button size="lg" className="bg-blue-600 hover:bg-blue-700" onClick={() => setShowModal(true)} data-testid="managed-it-hero-consult">
            <Calendar className="w-5 h-5 mr-2" />
            Book a Free Consult
          </Button>
          <p className="text-sm text-gray-600 mt-4">
            We respond within 1 business day (often faster). Emergency support available.
          </p>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">What's Included</h2>
          <p className="text-lg text-gray-600 text-center mb-12">
            Everything you need to keep your IT running smoothly
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {included.map((item) => {
              const Icon = item.icon;
              return (
                <Card key={item.id} className="border border-gray-200 hover:shadow-lg transition-all">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-gray-600">{item.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Response Times */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Response Times</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {responseTimes.map((item) => (
              <Card key={item.id} className="border-2 border-gray-200">
                <CardHeader className="text-center">
                  <CardTitle className="text-2xl text-blue-600">{item.title}</CardTitle>
                  <p className="text-lg font-semibold text-gray-900 mt-2">{item.time}</p>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-gray-600">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Month 1 Onboarding */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">Month 1 Onboarding</h2>
          <p className="text-lg text-gray-600 text-center mb-12">
            What happens in your first month with us
          </p>
          
          <div className="grid md:grid-cols-4 gap-8">
            {onboardingSteps.map((step) => (
              <div key={step.id} className="text-center">
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-white">{step.number}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-sm text-gray-600">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <Card key={index} className="border border-gray-200">
                <CardContent className="pt-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{faq.q}</h3>
                  <p className="text-gray-600">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Ready to Get Started?</h2>
          <p className="text-xl text-blue-50 mb-8">
            Let's discuss how managed IT can help your organization.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100" onClick={() => setShowModal(true)} data-testid="managed-it-cta-consult">
              <Calendar className="w-5 h-5 mr-2" />
              Book a Free Consult
            </Button>
            <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10">
              View Pricing
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <BookConsultModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </div>
  );
};

export default ManagedITPage;
